// src/pages/SuperAudioApprovalPage.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    Textarea,
    Flex,
    Spinner,
    useToast,
    Badge,
    VStack,
    HStack,
    Divider,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Tooltip,
    IconButton,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription
} from '@chakra-ui/react';
import {
    CheckIcon,
    CloseIcon,
    SearchIcon,
    DownloadIcon,
    TriangleDownIcon,
    RepeatClockIcon,
    InfoIcon
} from '@chakra-ui/icons';
import Sidebar from '../../components/Dashboard/Sidebar';
import ExpertFeedback from '../../components/Verification/ExpertFeedback';
import { fetchAllSurahs, getSignedUrl, downloadFile } from '../../api/quranService';
import {
    fetchAudioInfo,
    fetchAudioForApproval,
    approveAudio,
    revokeApproval,
    fetchApprovalStats
} from '../../api/audioVerificationService';

const RECITERS = {
    "1": "Alafasy",
    "2": "Abdul Baseet"
};

// ApprovalCard Component
const ApprovalCard = ({
    ayah,
    isApproved,
    notes,
    setNotes,
    onApprove,
    onRevoke,
    onPlayAudio,
    onDownload,
    approving,
    loading,
    selectedSurahId,
    selectedReciterId
}) => {
    const getBadgeProps = () => {
        if (isApproved) {
            if(ayah.approval_status?.approval_status){
                return { colorScheme: "green", text: "✓ Approved" };
            }
            return { colorScheme: "yellow", text: "Has Corrections" };
        }
        if (ayah.verification_summary?.is_fully_verified) {
            return { colorScheme: "blue", text: "Ready for Approval" };
        }
        if (ayah.verification_summary?.has_corrections) {
            return { colorScheme: "yellow", text: "Has Corrections" };
        }
        return { colorScheme: "gray", text: "Pending Verification" };
    };

    const badgeProps = getBadgeProps();

    return (
        <Card 
            border="2px" 
            borderColor={isApproved ? (ayah.approval_status?.approval_status ? "green.300" : "yellow.300") : "gray.200"}
            bg={isApproved ? (ayah.approval_status?.approval_status ? "green.50" : "yellow.50") : "white"}
        >
            <CardBody>
                <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                        <Text fontWeight="bold">
                            Ayah {ayah.ayah_index}
                        </Text>
                        <Badge colorScheme={badgeProps.colorScheme}>
                            {badgeProps.text}
                        </Badge>
                    </HStack>

                    <AyahContent ayah={ayah} />

                    <Divider />

                    <AudioControls
                        ayah={ayah}
                        onPlayAudio={onPlayAudio}
                        onDownload={onDownload}
                        selectedSurahId={selectedSurahId}
                        selectedReciterId={selectedReciterId}
                        loading={loading}
                    />

                    <Divider />

                    <VerificationSummary ayah={ayah} />

                    <ExpertFeedback summary={ayah.verification_summary} />

                    {isApproved && (
                        <ApprovalInfo ayah={ayah} />
                    )}

                    {!isApproved && (
                        <ApprovalNotesSection
                            ayahKey={ayah.ayah_key}
                            notes={notes}
                            setNotes={setNotes}
                        />
                    )}

                    <ApprovalActions
                        isApproved={isApproved}
                        ayahKey={ayah.ayah_key}
                        onApprove={onApprove}
                        onRevoke={onRevoke}
                        approving={approving}
                        canApprove={ayah.verification_summary?.total_verifications > 0}
                    />
                </VStack>
            </CardBody>
        </Card>
    );
};

// Sub-components
const AyahContent = ({ ayah }) => (
    <Box>
        <Text fontFamily="QuranFont" fontSize="2xl" textAlign="right" mb={3} dir="rtl">
            {ayah.recitation?.text}
        </Text>
        <Text fontSize="md" color="gray.700">
            {ayah.translations?.text || ""}
        </Text>
    </Box>
);

const AudioControls = ({ ayah, onPlayAudio, onDownload, selectedSurahId, selectedReciterId, loading }) => (
    <HStack>
        <Tooltip label="Play Audio">
            <IconButton
                size="sm"
                aria-label="Play verse"
                icon={<TriangleDownIcon transform="rotate(-90deg)" />}
                colorScheme="blue"
                onClick={() => onPlayAudio("audio/" + ayah.audio_url)}
                isDisabled={loading}
            />
        </Tooltip>
        <Tooltip label="Download Audio">
            <IconButton
                size="sm"
                aria-label="Download verse"
                icon={<DownloadIcon />}
                colorScheme="blue"
                onClick={() => onDownload(
                    "audio/" + ayah.audio_url,
                    `Surah_${selectedSurahId}_Verse_${ayah.ayah_index}_${RECITERS[selectedReciterId]}.mp3`
                )}
                isDisabled={loading}
            />
        </Tooltip>
    </HStack>
);

const VerificationSummary = ({ ayah }) => {
    const vs = ayah.verification_summary;
    
    return (
        <Box fontSize="sm" p={3} bg="gray.50" borderRadius="md">
            <Text fontWeight="bold" mb={2}>Expert Verification Summary:</Text>
            <HStack spacing={6}>
                <VStack align="start" spacing={1}>
                    <Text>Total Verifications: <strong>{vs?.total_verifications || 0}</strong></Text>
                    <Text color="green.600">Approved: <strong>{vs?.verified_count || 0}</strong></Text>
                </VStack>
                <VStack align="start" spacing={1}>
                    <Text color="yellow.600">With Corrections: <strong>{vs?.rejected_count || 0}</strong></Text>
                    <Text>Verification Rate: <strong>{vs?.verification_percentage?.toFixed(0) || 0}%</strong></Text>
                </VStack>
            </HStack>
        </Box>
    );
};


const ApprovalInfo = ({ ayah }) => (
    <Alert status="success" borderRadius="md">
        <AlertIcon />
        <Box flex="1">
            <AlertTitle>Approved by {ayah.approved_by_name || 'Super Expert'}</AlertTitle>
            <AlertDescription fontSize="sm">
                Approved on: {new Date(ayah.approved_at).toLocaleString()}
                {ayah.approval_notes && (
                    <Text mt={1} fontStyle="italic">Note: {ayah.approval_notes}</Text>
                )}
            </AlertDescription>
        </Box>
    </Alert>
);

const ApprovalNotesSection = ({ ayahKey, notes, setNotes }) => (
    <Box>
        <Text mb={2} fontWeight="medium">Approval Notes (optional):</Text>
        <Textarea
            value={notes[ayahKey] || ''}
            onChange={(e) =>
                setNotes((prev) => ({
                    ...prev,
                    [ayahKey]: e.target.value
                }))
            }
            placeholder="Add any notes about this approval..."
            size="sm"
        />
    </Box>
);

const ApprovalActions = ({ isApproved, ayahKey, onApprove, onRevoke, approving, canApprove }) => {
    if (isApproved) {
        return (
            <Button
                colorScheme="red"
                leftIcon={<RepeatClockIcon />}
                onClick={() => onRevoke(ayahKey)}
                isLoading={approving === ayahKey}
            >
                Revoke Approval
            </Button>
        );
    }

    return (
        <HStack>
            <Button
                colorScheme="green"
                leftIcon={<CheckIcon />}
                onClick={() => onApprove(ayahKey, true)}
                isLoading={approving === ayahKey}
                isDisabled={!canApprove}
                flex={1}
            >
                Approve as Final
            </Button>
            <Button
                colorScheme="orange"
                leftIcon={<CloseIcon />}
                onClick={() => onApprove(ayahKey, false)}
                isLoading={approving === ayahKey}
                isDisabled={!canApprove}
                flex={1}
            >
                Needs Revision
            </Button>
        </HStack>
    );
};

// Audio Player Component
const AudioPlayer = ({ currentAudio, audioRef, loading }) => {
    if (!currentAudio) return null;

    return (
        <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" bg="blue.50">
            <Flex justify="space-between" align="center" flexDirection={{ base: 'column', md: 'row' }}>
                <Text mb={{ base: 2, md: 0 }} fontWeight="medium">
                    {loading ? "Loading audio..." : "🎵 Now Playing"}
                </Text>
                <audio ref={audioRef} src={currentAudio} controls style={{ width: '100%', maxWidth: '500px' }} />
            </Flex>
        </Box>
    );
};

// Surah Selector Component
const SurahSelector = ({
    searchTerm,
    handleSearchChange,
    selectedSurahId,
    setSelectedSurahId,
    selectedReciterId,
    setSelectedReciterId,
    filteredSurahs,
    reciterInfos,
    loading
}) => (
    <Box maxW="lg" mb={8}>
        <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
                placeholder="Search Surah by name or number"
                value={searchTerm}
                onChange={handleSearchChange}
            />
        </InputGroup>

        <Select
            placeholder={loading ? "Loading Surah Data..." : "Select a Surah"}
            onChange={(e) => setSelectedSurahId(e.target.value)}
            value={selectedSurahId}
            mb={3}
        >
            {filteredSurahs.map((surah) => (
                <option key={surah._id} value={surah._id}>
                    {surah._id}. {surah.tname} ({surah.ename})
                </option>
            ))}
        </Select>

        <Select
            placeholder={loading ? "Loading Reciter Data..." : "Select Reciter"}
            onChange={(e) => setSelectedReciterId(e.target.value)}
            value={selectedReciterId}
        >
            {reciterInfos.map((info) => (
                <option key={info._id} value={info._id}>
                    {info.reciter_name}
                </option>
            ))}
        </Select>
    </Box>
);

// Stats Card Component
const StatsCard = ({ stats, loading }) => {
    if (loading || !stats) return null;

    return (
        <Card mb={6} bg="blue.50" borderLeft="4px" borderColor="blue.500">
            <CardBody>
                <Heading size="md" mb={4}>📊 Approval Statistics</Heading>
                <Flex gap={6} flexWrap="wrap">
                    <VStack align="start">
                        <Text fontSize="sm" color="gray.600">Total Recitations</Text>
                        <Text fontSize="2xl" fontWeight="bold">{stats.total_recitations}</Text>
                    </VStack>
                    <VStack align="start">
                        <Text fontSize="sm" color="gray.600">Approved</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                            {stats.total_approved}
                        </Text>
                    </VStack>
                    <VStack align="start">
                        <Text fontSize="sm" color="gray.600">Ready (100% Verified)</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                            {stats.pending_approval_100_verified}
                        </Text>
                    </VStack>
                    <VStack align="start">
                        <Text fontSize="sm" color="gray.600">With Corrections</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="yellow.600">
                            {stats.pending_with_corrections}
                        </Text>
                    </VStack>
                    <VStack align="start">
                        <Text fontSize="sm" color="gray.600">Approval Rate</Text>
                        <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                            {stats.approval_percentage.toFixed(1)}%
                        </Text>
                    </VStack>
                </Flex>
            </CardBody>
        </Card>
    );
};

// Main Component
const SuperAudioApprovalPage = () => {
    const toast = useToast();

    // State
    const [surahs, setSurahs] = useState([]);
    const [selectedSurahId, setSelectedSurahId] = useState('');
    const [filteredSurahs, setFilteredSurahs] = useState([]);
    const [reciterInfos, setReciterInfos] = useState([]);
    const [selectedReciterId, setSelectedReciterId] = useState('1');
    const [searchTerm, setSearchTerm] = useState('');
    const [ayahs, setAyahs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [approving, setApproving] = useState(null);
    const [notes, setNotes] = useState({});
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const [stats, setStats] = useState(null);

    const audioRef = React.useRef(null);

    const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const [surahsData, reciterData, statsData] = await Promise.all([
                    fetchAllSurahs(),
                    fetchAudioInfo(),
                    // fetchApprovalStats()
                ]);

                if (surahsData) {
                    setSurahs(surahsData?.surahs);
                    setFilteredSurahs(surahsData?.surahs);
                }

                if (reciterData) {
                    setReciterInfos(reciterData);
                }

                if (statsData) {
                    setStats(statsData);
                }
            } catch (err) {
                toast({
                    title: 'Failed to load initial data',
                    description: err.message || 'Something went wrong',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // Fetch ayahs when surah is selected
    useEffect(() => {
        if (!selectedSurahId) return;

        const fetchSurahAyahs = async () => {
            setLoading(true);
            try {
                const data = await fetchAudioForApproval(selectedSurahId, selectedReciterId);
                setAyahs(data.ayahs || []);
            } catch (err) {
                toast({
                    title: 'Error fetching ayahs',
                    description: err.message,
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };

        fetchSurahAyahs();
    }, [selectedSurahId, selectedReciterId]);

    // Event Handlers
    const handleApprove = async (ayahKey, status) => {
        setApproving(ayahKey);
        try {
            await approveAudio(ayahKey, selectedReciterId, status, notes[ayahKey]);
            
            toast({
                title: status ? 'Audio Approved!' : 'Marked for Revision',
                description: status 
                    ? 'This audio has been approved as final'
                    : 'This audio needs revision by experts',
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            // Refresh data
            const [updated, statsData] = await Promise.all([
                fetchAudioForApproval(selectedSurahId, selectedReciterId),
                // fetchApprovalStats()
            ]);
            
            setAyahs(updated.ayahs || []);
            setStats(statsData);
            setNotes(prev => ({ ...prev, [ayahKey]: '' }));
        } catch (err) {
            toast({
                title: 'Approval failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
        } finally {
            setApproving(null);
        }
    };

    const handleRevoke = async (ayahKey) => {
        setApproving(ayahKey);
        try {
            await revokeApproval(ayahKey, selectedReciterId);
            
            toast({
                title: 'Approval revoked',
                description: 'The approval has been removed',
                status: 'success',
                duration: 3000,
                isClosable: true
            });

            // Refresh data
            const [updated, statsData] = await Promise.all([
                fetchAudioForApproval(selectedSurahId, selectedReciterId),
                // fetchApprovalStats()
            ]);
            
            setAyahs(updated.ayahs || []);
            setStats(statsData);
        } catch (err) {
            toast({
                title: 'Revoke failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
        } finally {
            setApproving(null);
        }
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const filtered = surahs.filter(s =>
            s.ename.toLowerCase().includes(term.toLowerCase()) ||
            s.tname.toLowerCase().includes(term.toLowerCase()) ||
            s._id.toString().includes(term)
        );
        setFilteredSurahs(filtered);
    };

    const handlePlayAudio = async (url) => {
        try {
            setAudioLoading(true);
            const signedUrl = await getSignedUrl(url);
            setCurrentAudio(signedUrl);

            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play().catch(error => {
                        console.error("Audio playback failed:", error);
                        toast({
                            title: "Playback failed",
                            description: "Unable to play the audio file",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                        });
                    });
                }
            }, 100);
        } catch (err) {
            console.error('Failed to play audio:', err);
            toast({
                title: "Error",
                description: "Failed to play audio",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setAudioLoading(false);
        }
    };

    const handleDownload = async (url, fileName) => {
        try {
            setLoading(true);
            const key = extractKeyFromUrl(url);
            await downloadFile(key, fileName);

            toast({
                title: "Download started",
                description: `Downloading ${fileName || "audio file"}`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (err) {
            console.error('Download failed:', err);
            toast({
                title: "Download failed",
                description: "Unable to download the file",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const extractKeyFromUrl = (url) => {
        const matches = url.match(/amazonaws\.com\/(.+?)\?/);
        if (matches && matches[1]) {
            return matches[1];
        }
        return url;
    };

    // Filter ayahs based on status
    const fullyVerifiedAyahs = ayahs.filter(ayah => 
        !ayah.is_approved && 
        ayah.verification_summary?.is_fully_verified &&
        !ayah.verification_summary?.has_corrections
    );
    
    const withCorrectionsAyahs = ayahs.filter(ayah => 
        !ayah.is_approved && 
        ayah.verification_summary?.has_corrections
    );
    
    const approvedAyahs = ayahs.filter(ayah => ayah.is_approved);

    // Common props for ApprovalCard
    const cardProps = {
        notes,
        setNotes,
        onApprove: handleApprove,
        onRevoke: handleRevoke,
        onPlayAudio: handlePlayAudio,
        onDownload: handleDownload,
        approving,
        loading,
        selectedSurahId,
        selectedReciterId
    };

    return (
        <Flex direction="row" h="100vh">
            <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
            <Box
                pl={sidebarCollapsed ? "60px" : "250px"}
                w="full"
                overflowY="auto"
                pb={8}
                transition="padding-left 0.2s ease"
            >
                <Box p={8}>
                    <Heading mb={2}>Super Expert Audio Approval</Heading>
                    <Text color="gray.600" mb={6}>
                        Review and approve audio recitations that have been verified by experts
                    </Text>

                    <StatsCard stats={stats} loading={loading} />

                    <SurahSelector
                        searchTerm={searchTerm}
                        handleSearchChange={handleSearchChange}
                        selectedSurahId={selectedSurahId}
                        setSelectedSurahId={setSelectedSurahId}
                        selectedReciterId={selectedReciterId}
                        setSelectedReciterId={setSelectedReciterId}
                        filteredSurahs={filteredSurahs}
                        reciterInfos={reciterInfos}
                        loading={loading}
                    />

                    <AudioPlayer
                        currentAudio={currentAudio}
                        audioRef={audioRef}
                        loading={audioLoading}
                    />

                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : (
                        selectedSurahId && (
                            <Tabs variant="enclosed" colorScheme="teal">
                                <TabList>
                                    <Tab>
                                        ✓ Ready for Approval ({fullyVerifiedAyahs.length})
                                    </Tab>
                                    <Tab>
                                        ⚠️ With Corrections ({withCorrectionsAyahs.length})
                                    </Tab>
                                    <Tab>
                                        ✅ Completed ({approvedAyahs.length})
                                    </Tab>
                                </TabList>

                                <TabPanels>
                                    {/* Ready for Approval Tab - 100% Verified */}
                                    <TabPanel>
                                        {fullyVerifiedAyahs.length === 0 ? (
                                            <Alert status="info" borderRadius="md">
                                                <AlertIcon />
                                                <AlertDescription>
                                                    No ayahs are ready for approval yet. Experts need to verify them first.
                                                </AlertDescription>
                                            </Alert>
                                        ) : (
                                            <VStack spacing={6} align="stretch">
                                                {fullyVerifiedAyahs.map((ayah) => (
                                                    <ApprovalCard
                                                        key={ayah.ayah_key}
                                                        ayah={ayah}
                                                        isApproved={false}
                                                        {...cardProps}
                                                    />
                                                ))}
                                            </VStack>
                                        )}
                                    </TabPanel>

                                    {/* With Corrections Tab */}
                                    <TabPanel>
                                        {withCorrectionsAyahs.length === 0 ? (
                                            <Alert status="info" borderRadius="md">
                                                <AlertIcon />
                                                <AlertDescription>
                                                    No ayahs with corrections found in this surah.
                                                </AlertDescription>
                                            </Alert>
                                        ) : (
                                            <>
                                                <Alert status="warning" mb={4} borderRadius="md">
                                                    <AlertIcon />
                                                    <AlertDescription>
                                                        These ayahs have been flagged with corrections by experts. 
                                                        Review the expert comments before making your decision.
                                                    </AlertDescription>
                                                </Alert>
                                                <VStack spacing={6} align="stretch">
                                                    {withCorrectionsAyahs.map((ayah) => (
                                                        <ApprovalCard
                                                            key={ayah.ayah_key}
                                                            ayah={ayah}
                                                            isApproved={false}
                                                            {...cardProps}
                                                        />
                                                    ))}
                                                </VStack>
                                            </>
                                        )}
                                    </TabPanel>

                                    {/* Approved Tab */}
                                    <TabPanel>
                                        {approvedAyahs.length === 0 ? (
                                            <Alert status="info" borderRadius="md">
                                                <AlertIcon />
                                                <AlertDescription>
                                                    No ayahs have been approved yet in this surah.
                                                </AlertDescription>
                                            </Alert>
                                        ) : (
                                            <>
                                                <Alert status="success" mb={4} borderRadius="md">
                                                    <AlertIcon />
                                                    <Box>
                                                        <AlertTitle>Final Approvals</AlertTitle>
                                                        <AlertDescription>
                                                            These ayahs have been approved and are considered final. 
                                                            You can revoke an approval if needed.
                                                        </AlertDescription>
                                                    </Box>
                                                </Alert>
                                                <VStack spacing={6} align="stretch">
                                                    {approvedAyahs.map((ayah) => (
                                                        <ApprovalCard
                                                            key={ayah.ayah_key}
                                                            ayah={ayah}
                                                            isApproved={true}
                                                            {...cardProps}
                                                        />
                                                    ))}
                                                </VStack>
                                            </>
                                        )}
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )
                    )}
                </Box>
            </Box>
        </Flex>
    );
};

export default SuperAudioApprovalPage;